import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { useQuestionHandlers } from "@/lib/dnd/hooks/useQuestionHandlers";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useAppDispatch } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { getCorrectOrderIds } from "@ai-teach/canvas-core";
import {
  CANVAS_DEFAULT_FONT_FAMILY,
  MIN_ITEMS,
  answerColors,
  getOrderImageRows,
  getOrderImageStyles,
} from "@ai-teach/canvas-styles";
import type { OrderAnswer, OrderImageElement } from "@ai-teach/types";
import { useRef, useState } from "react";
import { AiFillPicture } from "react-icons/ai";
import { FaVolumeUp } from "react-icons/fa";
import { HiX } from "react-icons/hi";

interface OrderImageRendererProps {
  element: OrderImageElement;
  isLiveMode?: boolean;
  /** The sequence the student tapped in the play being replayed. */
  answer?: OrderAnswer | undefined;
  /** The question is over: a misplaced tile also shows the position it belonged at. */
  isFrozen?: boolean | undefined;
}

/**
 * Authoring/preview view of the image ordering question. Layout comes entirely from
 * `getOrderImageStyles`, the same function the mobile student renderer calls, so what the teacher
 * arranges here is what the student gets — tiles are uniform squares in a fixed-column grid, sized
 * by the element's frame plus `columns`/`imageScale` from the element toolbar. Nothing about a
 * tile is draggable or individually resizable; that freedom only ever produced layouts the phone
 * silently discarded.
 */
export function OrderImageRenderer({
  element,
  isLiveMode = false,
  answer,
  isFrozen,
}: OrderImageRendererProps) {
  const dispatch = useAppDispatch();
  const { handleQuestionDragAndDrop } = useQuestionHandlers();
  const { handleFileDrop } = useLocalImageUpload((media, containerId) =>
    handleQuestionDragAndDrop(media, containerId)
  );
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [tapOrder, setTapOrder] = useState<Record<string, number>>(() =>
    answer ? Object.fromEntries(answer.order.map((id, i) => [id, i + 1])) : {}
  );

  const { items, mediaType, media } = element;

  const styles = getOrderImageStyles(element.size.width, element.size.height, items.length, {
    columns: element.columns,
    imageScale: element.imageScale,
    mediaType,
    showHint: true,
  });

  const updateItems = (next: OrderImageElement["items"]) => {
    dispatch(updateElementProps({ id: element.id, props: { items: next } }));
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleRemoveItem = (index: number) => {
    if (items.length <= MIN_ITEMS) return;
    const remaining = items.filter((_, i) => i !== index);
    const orderMap = new Map(
      remaining
        .filter((it) => it.order !== undefined)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((it, i) => [it.id, i + 1])
    );
    updateItems(
      remaining.map((it) => (it.order !== undefined ? { ...it, order: orderMap.get(it.id) } : it))
    );
  };

  // Assign the next answer position to this tile, or clear it and close the gap it leaves.
  const handleSetOrder = (itemId: string) => {
    const current = items.find((it) => it.id === itemId)?.order;
    if (current === undefined) {
      const nextOrder = items.filter((it) => it.order !== undefined).length + 1;
      updateItems(items.map((it) => (it.id === itemId ? { ...it, order: nextOrder } : it)));
      return;
    }
    updateItems(
      items.map((it) => {
        if (it.id === itemId) return { ...it, order: undefined };
        if (it.order !== undefined && it.order > current) return { ...it, order: it.order - 1 };
        return it;
      })
    );
  };

  const handleTap = (itemId: string) => {
    if (!isLiveMode) return;
    setTapOrder((prev) => {
      if (prev[itemId] === undefined) return { ...prev, [itemId]: Object.keys(prev).length + 1 };
      const removedOrder = prev[itemId] ?? 0;
      const next: Record<string, number> = {};
      for (const [id, order] of Object.entries(prev)) {
        if (id === itemId) continue;
        next[id] = order > removedOrder ? order - 1 : order;
      }
      return next;
    });
  };

  // Same hint regardless of isLiveMode — used to show an editor-only string instead of what the student actually sees.
  const assignedCount = Object.keys(tapOrder).length;
  const hint =
    assignedCount === 0
      ? "Tap images in the correct order"
      : assignedCount < items.length
        ? `${assignedCount} / ${items.length} — tap a numbered image to deselect`
        : "Checking...";

  const correctOrderIds = getCorrectOrderIds(element);

  const renderTileContent = (item: OrderImageElement["items"][number], index: number) => {
    const liveOrder = tapOrder[item.id];
    const expectedNum = correctOrderIds.indexOf(item.id) + 1;
    const isMisplaced = !!isFrozen && liveOrder !== expectedNum;
    return (
      <>
        {item.media.src ? (
          <img
            src={item.media.src}
            alt={`Item ${index + 1}`}
            style={styles.tileImage}
            draggable={false}
          />
        ) : (
          <div
            style={{
              // The editor leaves the placeholder transparent so the tile's own drop tint shows
              // through it; the student view keeps the filled placeholder the phone renders.
              ...(isLiveMode ? styles.tileEmpty : { width: "100%", height: "100%" }),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!isLiveMode && (
              <AiFillPicture style={{ fontSize: styles.tileSize * 0.3, color: "#9ca3af" }} />
            )}
          </div>
        )}

        {isLiveMode && liveOrder !== undefined && (
          <div
            style={{
              ...styles.badge,
              ...(isFrozen
                ? {
                    backgroundColor: isMisplaced ? answerColors.wrong : answerColors.correct,
                  }
                : {}),
            }}
          >
            <span
              style={{
                fontSize: styles.badgeFontSize,
                fontWeight: 700,
                fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                color: "#fff",
              }}
            >
              {liveOrder}
            </span>
          </div>
        )}

        {isLiveMode && isMisplaced && (
          <div
            style={{
              ...styles.badge,
              left: styles.badge.right,
              right: undefined,
              backgroundColor: answerColors.correct,
            }}
          >
            <span
              style={{
                fontSize: styles.badgeFontSize,
                fontWeight: 700,
                fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
                color: "#fff",
              }}
            >
              {expectedNum}
            </span>
          </div>
        )}

        {!isLiveMode && (
          <button
            type="button"
            title="設定答案順序 Set answer order"
            onClick={(e) => {
              e.stopPropagation();
              handleSetOrder(item.id);
            }}
            style={{
              ...styles.badge,
              ...(item.order === undefined
                ? {
                    backgroundColor: "rgba(255,255,255,0.9)",
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: "#8b5cf6",
                  }
                : {}),
            }}
          >
            <span
              style={{
                fontSize: styles.badgeFontSize,
                fontWeight: 700,
                color: item.order === undefined ? "#8b5cf6" : "#fff",
              }}
            >
              {item.order ?? "+"}
            </span>
          </button>
        )}

        {!isLiveMode && items.length > MIN_ITEMS && (
          <button
            type="button"
            title="Remove image"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveItem(index);
            }}
            style={{
              ...styles.badge,
              left: styles.badge.top,
              right: undefined,
              backgroundColor: "#ef4444",
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <HiX style={{ fontSize: styles.badgeFontSize, color: "#fff" }} />
          </button>
        )}
      </>
    );
  };

  return (
    <div
      style={{
        ...styles.outerDiv,
        borderRadius: element.border.cornerRadius,
        backgroundColor: element.backgroundColor ?? "transparent",
      }}
    >
      {mediaType === "audio" && media?.src && (
        <audio
          ref={audioRef}
          src={media.src}
          onEnded={() => setIsPlaying(false)}
          className="hidden"
        >
          <track kind="captions" srcLang="en" default />
        </audio>
      )}

      {mediaType === "audio" && media?.src && (
        <button type="button" onClick={togglePlay} style={styles.audioButton}>
          <FaVolumeUp
            style={{ fontSize: styles.audioIconSize, color: isPlaying ? "#8b5cf6" : "#9ca3af" }}
          />
          <span style={{ ...styles.audioLabel, fontFamily: CANVAS_DEFAULT_FONT_FAMILY }}>
            {isPlaying ? "Playing..." : "Play Audio"}
          </span>
        </button>
      )}

      {mediaType === "video" && media?.src && (
        <video src={media.src} controls style={{ ...styles.video, objectFit: "contain" }}>
          <track kind="captions" srcLang="en" default />
        </video>
      )}

      <div style={styles.grid}>
        {getOrderImageRows(items, element.columns).map((row, rowIndex) => (
          <div key={`row-${row[0]?.id ?? rowIndex}`} style={styles.gridRow}>
            {row.map((item) => {
              const index = items.indexOf(item);
              return isLiveMode ? (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleTap(item.id)}
                  style={{
                    ...styles.tile,
                    ...(tapOrder[item.id] !== undefined ? styles.tileSelected : {}),
                  }}
                >
                  {renderTileContent(item, index)}
                </button>
              ) : (
                <DroppableZone
                  key={item.id}
                  id={`${element.id}-orderImage-${index}`}
                  data={{
                    id: `question//orderImage//${element.id}//${index}`,
                    type: "question-orderImage",
                  }}
                  allowedType={["image"]}
                  defaultBorderClass=""
                  className="group cursor-pointer"
                  style={styles.tile}
                  compatibleStyle={styles.tileDroppable}
                  overStyle={styles.tileDropTarget}
                  clickable
                  localFileAccept="image/*"
                  onLocalFileDrop={(file) =>
                    handleFileDrop(file, `question//orderImage//${element.id}//${index}`)
                  }
                >
                  {renderTileContent(item, index)}
                </DroppableZone>
              );
            })}
          </div>
        ))}
      </div>

      <div style={styles.hintRow}>
        <span style={{ ...styles.hintText, fontFamily: CANVAS_DEFAULT_FONT_FAMILY }}>{hint}</span>
      </div>
    </div>
  );
}
